import { Result, Schema } from "effect";
import { params, type FsrsParams } from "../../srs/fsrs.ts";
import { PositiveInt, Retention } from "../../srs/types.ts";

export const KindSettings = Schema.Struct({
  label: Schema.optionalKey(Schema.String),
  // TODO: Validate input against supported modes; typos silently grade a custom pinyin kind as meaning.
  input: Schema.optionalKey(Schema.String),
  paused: Schema.optionalKey(Schema.Boolean),
});
export type KindSettings = typeof KindSettings.Type;

export const Settings = Schema.Struct({
  kinds: Schema.Record(Schema.String, KindSettings),
  lessonBatchSize: PositiveInt,
  desiredRetention: Retention,
  pinned: Schema.Boolean,
});
export type Settings = typeof Settings.Type;

export const DEFAULT_LESSON_BATCH = PositiveInt.make(100);
export const DEFAULT_RETENTION = Retention.make(0.9);

const SettingsJson = Schema.fromJsonString(
  Schema.Struct({
    kinds: Schema.optionalKey(Schema.Record(Schema.String, KindSettings)),
    lesson_batch_size: Schema.optionalKey(PositiveInt),
    desired_retention: Schema.optionalKey(Retention),
    pinned: Schema.optionalKey(Schema.Boolean),
  }),
);

const decodeJson = Schema.decodeUnknownResult(SettingsJson);

export function decodeSettings(json: string): Result.Result<Settings, string> {
  return decodeJson(json).pipe(
    Result.map((s) => ({
      kinds: s.kinds ?? {},
      lessonBatchSize: s.lesson_batch_size ?? DEFAULT_LESSON_BATCH,
      desiredRetention: s.desired_retention ?? DEFAULT_RETENTION,
      pinned: s.pinned ?? false,
    })),
    Result.mapError((e) => e.message),
  );
}

export const paramsOf = (settings: Settings): FsrsParams => params(settings.desiredRetention);

// The kinds that show a Chinese word: Chinese to English (meaning) and Chinese to pinyin (pinyin).
export const HANZI_PROMPT_KINDS = ["meaning", "pinyin"] as const;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

// Change declared kinds only, preserving other settings; null means no pause state changed.
export function withKindsPaused(json: string, names: readonly string[], paused: boolean): string | null {
  const settings: unknown = JSON.parse(json);
  if (!isObject(settings) || !isObject(settings.kinds)) return null;
  let changed = false;
  for (const name of names) {
    const kind = settings.kinds[name];
    if (!isObject(kind) || (kind.paused === true) === paused) continue;
    if (paused) kind.paused = true;
    else delete kind.paused;
    changed = true;
  }
  return changed ? JSON.stringify(settings) : null;
}
